import * as React from 'react'
import { Plug } from 'lucide-react'
import type { StaffConnectionDto } from '@lib/dto/mcp.dto'
import { useAuth } from '@/auth/AuthContext'
import { AppCell, DeviceCell, EndAccessDialog, NoConnectedApps, whenLabel } from '@/components/mcp/ConnectionParts'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useMcpEnabled, useMcpStatus, useOpsMcpActions, useStaffConnections } from '@/hooks/useConnectedApps'

interface ConnectionsTableProps {
  connections: StaffConnectionDto[]
  /** Whether the signed-in staff member may end this connection (their own, or any for admins). */
  canEnd: (connection: StaffConnectionDto) => boolean
  onEnd: (connection: StaffConnectionDto) => void
}

function ConnectionsTable({ connections, canEnd, onEnd }: ConnectionsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Staff</TableHead>
          <TableHead>App</TableHead>
          <TableHead>Access</TableHead>
          <TableHead>Signed in from</TableHead>
          <TableHead>Last used</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {connections.map((connection) => (
          <TableRow key={connection.id}>
            <TableCell>
              <div className="font-medium">{connection.staff.name}</div>
              <div className="text-caption text-muted-foreground">{connection.staff.email}</div>
            </TableCell>
            <TableCell>
              <AppCell app={connection.app} />
            </TableCell>
            <TableCell>{connection.canChange ? <Badge tone="warning">Make changes</Badge> : 'Read only'}</TableCell>
            <TableCell>
              <DeviceCell device={connection.device} signInIp={connection.signInIp} />
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">{whenLabel(connection.lastUsedAt)}</TableCell>
            <TableCell className="text-right">
              {canEnd(connection) ? (
                <Button variant="destructive-quiet" size="sm" onClick={() => onEnd(connection)}>
                  Disconnect
                </Button>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Staff AI app connections. Support staff see and end their own; admins see
 * everyone's and can end any. Ending one stops its tokens at once.
 */
export function OpsConnectedAppsPage() {
  const { user } = useAuth()
  const status = useMcpStatus()
  const enabled = useMcpEnabled()
  const query = useStaffConnections({ enabled })
  const actions = useOpsMcpActions()
  const [ending, setEnding] = React.useState<StaffConnectionDto | null>(null)
  const isAdmin = user?.role === 'ADMIN'
  const endingOwn = ending?.staff.id === user?.id

  function renderBody() {
    if (status.isLoading) return <LoadingState />
    if (!enabled) {
      return <EmptyState icon={Plug} title="Not available yet" description="Connecting AI apps to PixelSign isn’t switched on yet." />
    }
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) return <ErrorState message={apiErrorMessage(query.error, 'Could not load the connected apps.')} />
    if (query.data.connections.length === 0) return <NoConnectedApps />
    return (
      <ConnectionsTable
        connections={query.data.connections}
        canEnd={(connection) => isAdmin || connection.staff.id === user?.id}
        onEnd={setEnding}
      />
    )
  }

  return (
    <>
      <PageHeader
        title="Connected apps"
        description={
          isAdmin
            ? 'AI apps PixelSign staff have connected, and whether they can make changes.'
            : 'AI apps you’ve connected to PixelSign as staff, and whether they can make changes.'
        }
      />
      {renderBody()}
      <EndAccessDialog
        open={ending !== null}
        title={endingOwn ? `Disconnect ${ending?.app.name ?? 'this app'}?` : `Disconnect ${ending?.staff.name ?? 'their'}’s ${ending?.app.name ?? 'app'}?`}
        description={`${ending?.app.name ?? 'The app'} loses access to PixelSign right away.${endingOwn ? ' You can connect it again later.' : ''}`}
        confirmLabel="Disconnect"
        onClose={() => setEnding(null)}
        onConfirm={async () => {
          if (!ending) return
          await actions.disconnect.mutateAsync(ending.id)
          toast.success(`Disconnected ${ending.app.name}`)
        }}
      />
    </>
  )
}
